import { type Address } from 'viem';
import { fetchEnvironmentConfig } from '../../../config/environment';

// Contract addresses (dynamic based on environment)
// Production: BSC Mainnet (ChainID: 56)
// Development: Sepolia Testnet (ChainID: 11155111) - displayed as "BSC Mainnet" in UI

/**
 * Get contract addresses based on environment (dev/prod)
 * Fetches from backend config API
 */
export async function getSwapAddresses() {
  const config = await fetchEnvironmentConfig();
  return {
    OLD_FAR_TOKEN: config.contracts.swap.oldToken as Address,
    NEW_FAR_TOKEN: config.contracts.swap.newToken as Address,
    SWAP_CONTRACT: config.contracts.swap.swapContract as Address,
    CHAIN_ID: config.contracts.swap.chainId,
    CHAIN_NAME: config.contracts.swap.chainName,
  };
}

// Fallback constants for synchronous access (production values)
export const OLD_FAR_TOKEN: Address = '0xDC816B6bd4786720e91Eb17D4DaBf0E1eEf5eE91'; // Old FAR token to be swapped
export const NEW_FAR_TOKEN: Address = '0xC44F9f08E524669E5DEeEBC9EB142c81edfAd178'; // FAR (main BSC token)
export const SWAP_CONTRACT: Address = '0xBA72ccedd7b94CDFCAE8107F0EB23a1b88A35756'; // Token SWAP contract

// ERC20 ABI (minimal - just what we need)
export const ERC20_ABI = [
  {
    inputs: [{ name: 'account', type: 'address' }],
    name: 'balanceOf',
    outputs: [{ name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      { name: 'spender', type: 'address' },
      { name: 'amount', type: 'uint256' },
    ],
    name: 'approve',
    outputs: [{ name: '', type: 'bool' }],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      { name: 'owner', type: 'address' },
      { name: 'spender', type: 'address' },
    ],
    name: 'allowance',
    outputs: [{ name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
] as const;

// Token Swap Contract ABI - REAL functions from verified contract
export const SWAP_ABI = [
  {
    inputs: [{ name: 'amount', type: 'uint256' }],
    name: 'swap',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [],
    name: 'getStatistics',
    outputs: [
      { name: 'totalSwappedAmount', type: 'uint256' },
      { name: 'totalUsersCount', type: 'uint256' },
      { name: 'availableNewTokens', type: 'uint256' },
      { name: 'lockedOldTokens', type: 'uint256' },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [{ name: 'user', type: 'address' }],
    name: 'getUserInfo',
    outputs: [
      { name: 'hasSwappedTokens', type: 'bool' },
      { name: 'swappedAmount', type: 'uint256' },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'getAvailableNewTokens',
    outputs: [{ name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'swapActive',
    outputs: [{ name: '', type: 'bool' }],
    stateMutability: 'view',
    type: 'function',
  },
] as const;

// Type exports
export type SwapStatistics = readonly [bigint, bigint, bigint, bigint];
export type UserInfo = readonly [boolean, bigint];
